import { UniverseService } from '@/domain/universes/universe.service';
import { injectModalInput } from '@/services/advizeo-modal.service';
import { SharedUiColorName, SharedUiColorsService } from '@/shared/ui/theming';
import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiCheckboxComponent } from '@/ui/checkbox/ui-checkbox';
import { UiFormErrorComponent } from '@/ui/form-field/form-error/ui-form-error';
import { UiFormFieldComponent } from '@/ui/form-field/form-field/ui-form-field';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiRadioButtonComponent } from '@/ui/radio-button/ui-radio-button';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { isDirtyAndHasError } from '@/utils/angular/forms/reactive-forms/form-state.utils';
import { ControlValueAccessorInteropDirective } from '@/utils/angular/forms/reactive-forms/reactive-forms-signals-interop.utils';
import { positiveNumberValidator } from '@/utils/angular/forms/reactive-forms/validators/positive-number.validator';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { deepCopy } from '@/utils/toolkit/object/copy.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { ChangeDetectionStrategy, Component, type OnInit, forwardRef, inject, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { filter, firstValueFrom } from 'rxjs';
import { TileIpeGaugeSubComponentComponent } from '../../components/tile/tile-ipe-gauge/tile-ipe-gauge-sub-component/tile-ipe-gauge-sub-component';
import { TileIpeValueSubComponentComponent } from '../../components/tile/tile-ipe-value/tile-ipe-value-sub-component/tile-ipe-value-sub-component';
import { AdvizeoBooleanControlGroupDirective } from '../../directives/advizeo-boolean-controls-group/advizeo-boolean-control-group.directive';
import { AdvizeoBooleanControlDirective } from '../../directives/advizeo-boolean-controls-group/advizeo-boolean-control.directive';
import { AdvizeoInputNumberDirective } from '../../directives/advizeo-input/advizeo-input-number.directive';
import { AdvizeoInputDirective } from '../../directives/advizeo-input/advizeo-input.directive';
import type { ModalComponent } from '../../interfaces/Modal.interface';
import {
    type IpeHistoryTileData,
    type IpeTileData,
    type IpeTileDescription,
    type StepTile,
    type TypedTileData,
    defaultIpeTile
} from '../../interfaces/Tile.interface';
import { VatModes } from '../../providers/budgets/budgets.constants';
import { InformationProvider } from '../../providers/information/information.provider';
import { IpeHistoryProvider } from '../../providers/ipe-history/ipe-history.provider';
import { EnergyType, IpeStaticProvider } from '../../providers/ipe.static.provider';
import { ProjectsProvider } from '../../providers/projects/projects.provider';
import { TileConstants } from '../../providers/tiles/tiles.constants';
import { TilesProvider } from '../../providers/tiles/tiles.provider';
import { TilesStaticProvider } from '../../providers/tiles/tiles.static.provider';

export enum Unit {
    KWH = 'kwh',
    M3 = 'm3',
    KG = 'kg',
    EURO = 'euro'
}

export interface IpeTileCreationPopoverParams {
    dateStart: moment.Moment;
    dateEnd: moment.Moment;
    editMode: boolean;

    // Only in edit mode
    tile?: any;
    // Only in creation mode
    zone?: string;
    buildingId?: string;
}

export interface IpeTileCreationOutput {
    tile: TypedTileData<IpeTileDescription, number, string>;
    edit: boolean;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./ipe-tile-creation.scss'],
    selector: 'ipe-tile-creation',
    templateUrl: 'ipe-tile-creation.html',
    imports: [
        UiIconButtonComponent,
        UiFormFieldComponent,
        AdvizeoInputDirective,
        FormsModule,
        AdvizeoBooleanControlGroupDirective,
        UiRadioButtonComponent,
        AdvizeoBooleanControlDirective,
        AdvizeoInputNumberDirective,
        UiCheckboxComponent,
        forwardRef(() => TileIpeValueSubComponentComponent),
        forwardRef(() => TileIpeGaugeSubComponentComponent),
        UiButtonComponent,
        ReactiveFormsModule,
        UiSelectComponent,
        UiFormErrorComponent,
        TranslatePipe,
        ControlValueAccessorInteropDirective
    ]
})
export class IpeTileCreationPopover implements ModalComponent<IpeTileCreationPopoverParams, IpeTileCreationOutput>, OnInit {
    private _translateProvider = inject(TranslateService);
    private _universeService = inject(UniverseService);
    private _ipeHistoryProvider = inject(IpeHistoryProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _formBuilder = inject(UntypedFormBuilder);
    private _tileProvider = inject(TilesProvider);
    private _projectsProvider = inject(ProjectsProvider);
    private _informationProvider = inject(InformationProvider);
    private _colorsService = inject(SharedUiColorsService);

    steps: StepTile[] = [];
    tile!: IpeTileData;
    editMode: boolean = false;
    currentStep = 0;
    nbSteps: number = 0;
    allowUnknown: boolean = true;
    forceBinding: boolean = true;

    dateStart!: moment.Moment;
    dateEnd!: moment.Moment;
    CUSTOMCONSTANT = 'CUSTOM_CONSTANT';
    COLOR = '#FF0000';

    IPE_VALUE: string = '';
    IPE_GAUGE: string = '';
    CONSTANT_1: string = '';

    rangeChoices = {
        customRanges: 'CUSTOM_RANGES',
        yearsRanges: 'DEFINE_RANGES'
    };

    rangeChoicesSelected!: string;
    referenceYears: number[] = [];
    referenceYearsOptions: { label: number; value: number }[] = [];
    tileHistory!: IpeHistoryTileData;
    currentYear!: number;

    defineRangesForm!: UntypedFormGroup;
    itHasError = isDirtyAndHasError;

    vatMode!: VatModes;

    input = injectModalInput<IpeTileCreationPopoverParams>();
    close = output<IpeTileCreationOutput | undefined>();

    ngOnInit() {
        // Init date from param
        this.dateStart = this.input.dateStart;
        this.dateEnd = this.input.dateEnd;
        this.editMode = this.input.editMode;

        // Get constant
        this.IPE_VALUE = TileConstants.IPE_VALUE;
        this.IPE_GAUGE = TileConstants.IPE_GAUGE;
        this.CONSTANT_1 = TileConstants.CONSTANT_1;

        // Init of the different steps
        this.steps = deepCopy(IpeStaticProvider.stepsCreation);

        // Init of the number of step
        this.nbSteps = this.steps.length;

        // Init reference year list
        this.currentYear = moment().year();

        this._universeService.waitForUniverseLoad().then(() => {
            // Init universe options
            this._universeService
                .getAllUniversesR()
                .filter(universe => universe.isBillable)
                .forEach(universe => {
                    const matchingUniverse = this._referenceListsService.getReferenceListItemLabelR(
                        ReferenceListName.UNIVERSE,
                        universe.name
                    );
                    const label = matchingUniverse ? matchingUniverse : universe.name.toUpperCase();
                    this.steps[3].options.push({
                        label,
                        value: universe.name,
                        unit: universe.baseUnitCode,
                        isEnergyUniverse: this._universeService.isEnergyUniverse(universe.name)
                    });
                });

            // Init of the tile -- Edition
            if (this.editMode) {
                this.tile = deepCopy(this.input.tile);
                if (
                    this.tile.description &&
                    this.tile.description.energyType !== EnergyType.CURRENCY &&
                    this.steps[3] &&
                    this.steps[3].options &&
                    Array.isArray(this.steps[3].options)
                ) {
                    this.steps[3].options = this.steps[3].options.filter(option => option.isEnergyUniverse);
                }
                this.tile.description.ranges.sort((rangeA, rangeB) => {
                    return rangeA.start - rangeB.start;
                });
                this.currentStep = 1;
                if (this.tile && this.tile.description && this.tile.description.indicator) {
                    this.setStep2Labels(this.tile.description.indicator);
                }

                // Init range properties
                this.rangeChoicesSelected = this.tile.description.yearsRanges
                    ? this.rangeChoices.yearsRanges
                    : this.rangeChoices.customRanges;
            } else {
                //  -- Creation
                this.tile = deepCopy(defaultIpeTile);
                this.tile.buildingId = this.input.buildingId!;
                this.tile.tileGroup = this.input.zone!;
                // Default value
                this.rangeChoicesSelected = this.rangeChoices.yearsRanges;
                this.tile.description.yearsRanges = true;
            }

            this.initForm();
        });

        this._projectsProvider
            .getVatMode$()
            .pipe(untilDestroyed(this), filter(isNotNullish))
            .subscribe(vatMode => {
                this.vatMode = vatMode;
            });
    }

    initForm() {
        const yearRegex = /^[0-9]{1,4}$/;

        this.defineRangesForm = this._formBuilder.group({
            referenceYear: [this.tile.description.referenceYear, Validators.required],
            targetYear: [
                this.tile.description.targetYear,
                Validators.compose([
                    Validators.required,
                    positiveNumberValidator,
                    Validators.min(this.currentYear),
                    Validators.pattern(yearRegex) // Year validator
                ])
            ],
            targetYearRatio: [
                this.tile.description.targetYearRatio,
                Validators.compose([Validators.required, positiveNumberValidator, Validators.min(0), Validators.max(100)])
            ]
        });
    }

    // First step without the 'next' button
    typeChoice(option: any) {
        this.tile.tileType = option.value;
        this.currentStep++;
    }

    nextStep() {
        if (this.currentStep === 1) {
            if (
                this.tile &&
                this.tile.description &&
                this.tile.name !== '' &&
                this.tile.tileType !== '' &&
                this.tile.description.energyType !== ''
            ) {
                if (this.tile.description.indicator === this.CUSTOMCONSTANT) {
                    if (this.tile.description.customIndicator.label !== '' && this.tile.description.customIndicator.value !== 0) {
                        this.currentStep = this.currentStep + 1;
                    } else {
                        this._informationProvider.showToast('IPE.MISSING_INFORMATION', 'error');
                    }
                } else {
                    this.currentStep = this.currentStep + 1;
                }
            } else {
                this._informationProvider.showToast('IPE.MISSING_INFORMATION', 'error');
            }
        } else if (this.currentStep === 2) {
            if (this.tile && this.tile.description && this.tile.description.universeIds && this.tile.description.universeIds.length > 0) {
                this.currentStep = this.currentStep + 1;
                this.getPreviewHistory();
            } else {
                this._informationProvider.showToast('IPE.MISSING_INFORMATION', 'error');
            }
        } else if (this.currentStep === 3) {
            if (this.tile && this.tile.description && this.tile.description.ranges && this.tile.description.ranges.length > 0) {
                this.currentStep = this.currentStep + 1;
            } else {
                this._informationProvider.showToast('IPE.MISSING_INFORMATION', 'error');
            }
        }
    }

    change(step: number, event: any) {
        if (!event) {
            return;
        }

        // Step 1: Choice of consumption
        if (step === 1) {
            // Set the Step 2 's options depending of the consumption choice
            this.tile.description.energyType = '';
            this.tile.description.indicator = event;
            this.steps[2] = deepCopy(IpeStaticProvider.stepEnergy);
            this.setStep2Labels(event);
        }

        // Step 2: Choice of the energy type
        else if (step === 2) {
            // Get the energyType
            this.tile.description.energyType = event;

            // Set the the step 3 's options depending on the energyType
            this.resetOptions();

            // Get the unit's option depending of the value selected
            this.tile.description.unit = this.steps[2].options.filter(option => option.value === this.tile.description.energyType)[0].unit;
        } else if (step === 3) {
            // get the list of universeIds and launch the request to get the IPE value
            this.tile.description.universeIds = event;
            if (this.tile.description.universeIds.length > 0) {
                this.getTilePreview();
                this.getPreviewHistory();
            }
        }
    }

    private setStep2Labels(indicator: string) {
        if (this.steps[2] && Array.isArray(this.steps[2].options)) {
            this.steps[2].options.forEach(option => {
                if (this.steps[1].options.filter(option => option.value === indicator)[0]) {
                    if (this.tile.description.indicator === this.CONSTANT_1) {
                        option.label =
                            this._translateProvider.instant(option.label) +
                            ' (' +
                            this._tileProvider.getDisplayedTypedUnit(option.unit, option.value, false, this.vatMode) +
                            ')';
                    } else if (this.tile.description.indicator === this.CUSTOMCONSTANT) {
                        if (this.tile.description.customIndicator && this.tile.description.customIndicator.label !== '') {
                            option.label =
                                this._translateProvider.instant(option.label) +
                                ' (' +
                                this._tileProvider.getDisplayedTypedUnit(option.unit, option.value, false, this.vatMode) +
                                '/' +
                                this.tile.description.customIndicator.label +
                                ')';
                        }
                    } else {
                        option.label =
                            this._translateProvider.instant(option.label) +
                            ' (' +
                            this._tileProvider.getDisplayedTypedUnit(option.unit, option.value, false, this.vatMode) +
                            '/' +
                            this._tileProvider.getDisplayedUnit(
                                this._translateProvider.instant(this.steps[1].options.filter(option => option.value === indicator)[0].unit)
                            ) +
                            ')';
                    }
                }
            });
        }
    }

    indicatorChange(indic: 'label' | 'value', event: string) {
        if (indic === 'value') {
            this.tile.description.customIndicator.value = Number(event);
        } else if (indic === 'label') {
            this.tile.description.customIndicator.label = event;
        }
        if (this.tile.description.indicator === this.CUSTOMCONSTANT && this.tile.description.customIndicator.label !== '') {
            this.steps[2] = deepCopy(IpeStaticProvider.stepEnergy);
            this.steps[2].options.forEach(option => {
                option.label =
                    this._translateProvider.instant(option.label) +
                    ' (' +
                    this._tileProvider.getDisplayedUnit(option.unit) +
                    '/' +
                    this.tile.description.customIndicator.label +
                    ')';
            });
        }
    }

    resetOptions() {
        this.steps[3].options = [];
        this._universeService
            .getAllUniversesR()
            .filter(universe => universe.isBillable)
            .forEach(universe => {
                const matchingUniverse = this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIVERSE, universe.name);
                const label = matchingUniverse ? matchingUniverse : universe.name.toUpperCase();
                this.steps[3].options.push({
                    label,
                    value: universe.name,
                    unit: universe.baseUnitCode,
                    isEnergyUniverse: this._universeService.isEnergyUniverse(universe.name)
                });
            });

        if (this.tile.description.energyType === EnergyType.CUBIC_METER) {
            this.steps[3].options = this.steps[3].options.filter(option => option.unit === Unit.M3);
        } else if (this.tile.description.energyType === EnergyType.FINAL || this.tile.description.energyType === EnergyType.PRIMARY) {
            this.steps[3].options = this.steps[3].options.filter(option => option.isEnergyUniverse);
        }

        this.tile.description.universeIds = [];
        this.allowUnknown = false;
    }

    /* ---- range functions ---- */
    rangeTypeChange($event: string) {
        this.rangeChoicesSelected = $event;
        this.tile.description.yearsRanges = this.rangeChoicesSelected === this.rangeChoices.yearsRanges;

        if (!this.tile.description.yearsRanges) {
            let startValue = 0;

            this.tile.description.ranges.forEach(option => {
                option.start = Number(startValue.toFixed(0));
                startValue = Number((startValue + (this.tile.value / 3) * 2).toFixed(0));
                option.end = Number(startValue.toFixed(0));
            });

            this.changeChart();
        } else {
            this.updateReferenceRanges();
        }
    }

    initReferenceData() {
        const defaultReferenceYear = moment().subtract(1, 'year').year();

        if (this.referenceYears.length > 0) {
            const minYear = Math.min(...this.referenceYears);
            this.tile.description.referenceYear = minYear < this.currentYear ? minYear : defaultReferenceYear;
        } else {
            // If no data
            this.tile.description.referenceYear = defaultReferenceYear;
        }
        // By default + 20 years
        this.tile.description.targetYear = moment().year() + 20;
        this.tile.description.targetYearRatio = 20;
    }

    async updateReferenceRanges() {
        let referenceYearData;
        if (this.tileHistory && this.tileHistory.value && this.tileHistory.value.length > 0) {
            referenceYearData = this.tileHistory.value.find(data => moment(data.period).year() === this.tile.description.referenceYear);
        }

        let ranges;
        if (referenceYearData) {
            const highBound = Math.round(referenceYearData.value);
            const lowBound = Math.round(highBound - highBound * (this.tile.description.targetYearRatio / 100));

            const maxBoundCalculate = highBound + Math.round(highBound * (2 / 3));
            const maxBound = maxBoundCalculate < this.tile.value ? this.tile.value : maxBoundCalculate;

            ranges = [
                {
                    start: 0,
                    end: lowBound,
                    color: await firstValueFrom(this._colorsService.getColoredBackgroundColor$({ color: SharedUiColorName.SUCCESS }))
                },
                {
                    start: lowBound,
                    end: highBound,
                    color: await firstValueFrom(this._colorsService.getColoredBackgroundColor$({ color: SharedUiColorName.DANGER }))
                },
                {
                    start: highBound,
                    end: maxBound,
                    color: await firstValueFrom(this._colorsService.getColoredBackgroundColor$({ color: SharedUiColorName.FAILURE }))
                }
            ];
        } else {
            ranges = [
                {
                    start: 0,
                    end: 1,
                    color: '#6F6F6F'
                }
            ];
        }
        this.tile.description.ranges = ranges;
        this.changeChart();
    }

    addRange() {
        if (this.tile && this.tile.description && this.tile.description.ranges && this.tile.description.ranges.length < 10) {
            let diff = 0;
            let lastValue = 0;
            this.tile.description.ranges.forEach(option => {
                if (option.start && option.end) {
                    diff = option.end - option.start;
                    lastValue = option.end;
                }
            });
            this.tile.description.ranges.push({
                start: lastValue,
                end: lastValue + diff,
                color: this.COLOR
            });
            this.changeChart();
        }
    }

    deleteRange(optionToDelete: any) {
        this.forceBinding = !this.forceBinding;
        this.tile.description.ranges = this.tile.description.ranges.filter(option => option !== optionToDelete);
    }

    changeChart() {
        this.forceBinding = !this.forceBinding;
    }

    changeStart(option: any) {
        const index: number = this.tile.description.ranges.indexOf(option);
        if (index && this.tile.description.ranges[index - 1]) {
            this.tile.description.ranges[index - 1].end = option.start;
        }
        this.changeChart();
    }

    changeEnd(option: any) {
        const index: number = this.tile.description.ranges.indexOf(option);
        if (index && this.tile.description.ranges[index + 1]) {
            this.tile.description.ranges[index + 1].start = option.end;
        }
        this.changeChart();
    }
    /* ---- END OF range functions ---- */

    /* ---- API CALL ---- */
    createTile() {
        let body = this.getIpeBody();
        body = {
            ...body,
            buildingId: this.tile.buildingId,
            tileGroup: this.tile.tileGroup
        };

        this._tileProvider
            .createTile(body)
            .then(result => {
                this._informationProvider.showToast('IPE.CREATE_SUCCESS', 'success');

                this.tile.tileId = result;
                const data = {
                    tile: this.tile,
                    edit: false
                };

                this.close.emit(data);
            })
            .catch(err => {
                this._informationProvider.showToast('IPE.CREATE_ERROR', 'error');
                console.error('error while creating tile', err);
            });
    }

    editTile() {
        const body = this.getIpeBody();

        this._tileProvider
            .updateTile(this.tile.tileId, body)
            .then(() => {
                this._informationProvider.showToast('IPE.EDIT_SUCCESS', 'success');

                const data = {
                    tile: this.tile,
                    edit: true
                };
                this.close.emit(data);
            })
            .catch(() => {
                this._informationProvider.showToast('IPE.EDIT_ERROR', 'error');
            });
    }

    getTilePreview() {
        // Interface to create a Tile
        const body = this.getIpePreviewBody();

        // Get the value of the preview and set the range value
        this._tileProvider.getTilePreview(body, this.dateStart, this.dateEnd).then(result => {
            if (result.error) {
                this.tile.error = result.error;
                this.tile.value = null as unknown as number;
            } else if (Number(result.value.toFixed(0)) !== this.tile.value) {
                this.tile.error = null as unknown as string;
                this.tile.value = Number(result.value.toFixed(0));
            }
        });
    }

    getPreviewHistory() {
        const body: any = this.getIpePreviewBody();
        body['yearsRanges'] = this.rangeChoicesSelected === this.rangeChoices.yearsRanges;
        body['referenceYear'] = this.tile.description.referenceYear;
        body['targetYear'] = this.tile.description.targetYear;
        body['targetYearRatio'] = this.tile.description.targetYearRatio;

        this._ipeHistoryProvider
            .getIpePreviewHistory(body)
            .then(th => {
                this.tileHistory = th;
                if (th.value && th.value.length > 0) {
                    // init referenceYears
                    this.referenceYears = th.value
                        .sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime())
                        .filter(data => moment().year() > moment(data.period).year() && data.error === null)
                        .map(data => moment(data.period).year());

                    this.referenceYearsOptions = this.referenceYears.map(year => {
                        return {
                            label: year,
                            value: year
                        };
                    });

                    // Creation
                    if (!this.editMode) {
                        this.initReferenceData();
                        this.updateReferenceRanges();
                    }

                    if (this.editMode && this.rangeChoicesSelected === this.rangeChoices.yearsRanges) {
                        this.updateReferenceRanges();
                    }
                }
            })
            .catch(err => {
                this.referenceYears = [];
                console.error('error while fetching ipe history preview', err);
            });
    }

    getIpeBody() {
        if (!this.tile) return;
        return {
            name: this.tile.name,
            type: TilesStaticProvider.getIpeRealTypeUsingIndicator(this.tile),
            universeIds: this.tile.description.universeIds,
            customIndicator: this.tile.description.customIndicator,
            unit: this.tile.description.unit,
            energyType: this.tile.description.energyType,
            indicator: this.tile.description.indicator,
            ranges: this.tile.description.ranges,
            yearsRanges: this.rangeChoicesSelected === this.rangeChoices.yearsRanges,
            referenceYear: this.tile.description.referenceYear,
            targetYear: this.tile.description.targetYear,
            targetYearRatio: this.tile.description.targetYearRatio
        } as any;
    }

    getIpePreviewBody() {
        if (!this.tile) return;
        return {
            buildingId: this.tile.buildingId,
            type: TilesStaticProvider.getIpeRealTypeUsingIndicator(this.tile),
            universeIds: this.tile.description.universeIds,
            customIndicator: this.tile.description.customIndicator,
            unit: this.tile.description.unit,
            energyType: this.tile.description.energyType,
            indicator: this.tile.description.indicator,
            isVatIncl: this.vatMode === VatModes.VAT_INCL
        };
    }

    needsValuePreview(tile: IpeTileData) {
        return TilesStaticProvider.isIpeValue(tile);
    }

    needsGaugePreview(tile: IpeTileData) {
        return TilesStaticProvider.isIpeGauge(tile);
    }
}
